/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react'
import { Layout, Table, Button } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import sensors, { sensorsRole } from '../../utils/sensors'
import Filter, { AttendanceFilter } from './Filter'
import CONFIG from '../../constants/config'
import {
  fetchAttendancePeriod,
  fetchAttendanceExcel
} from '../../actions/attendance'
import { fetchPhotoWatermarkExcel } from '../../actions/photoWatermark'
import * as SentryUtils from '../../utils/sentryUtils'
import { setPaymentVisible } from '../../actions/main'
import vip_download_excel_icon from '../../images/vip_download_excel.png'
import baseColumnsData from '../photoWatermark/lib/baseColumnsData'
import { useAppDispatch, useAppSelector } from '../../app/hooks'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout
const { Column } = Table
const PERIOD_EXPORT_TYPE = 1
const DAILY_EXPORT_TYPE = 0
const PHOTO_WATERMARK_EXPORT_TYPE = 2

// 打卡时间类型
interface WorkTime {
  photoTime?: string
  status?: number
}

// 时间段类型
interface TimeBucket {
  onWork?: WorkTime
  offWork?: WorkTime
}

// 考勤记录类型
interface AttendanceRecord {
  date: string
  status?: number
  bucket?: TimeBucket[]
  ruleChunkID?: string
}

// 周月统计数据类型
interface PeriodData {
  userID: string
  ruleChunkName?: string
  totalPhotoCount?: number
  onworkDay?: number
  unworkDay?: number
  totalWorkSecond?: number
  lateCount?: number
  leaveEarlyCount?: number
  cardShortageCount?: number
  abnormalWorkingHoursCount?: number
  leaveCount?: number
  leaveDurationText?: string
  records?: AttendanceRecord[]
}

// 用户类型
interface RecordUser {
  userID: string
  userName: string
}

// Excel 任务类型
interface ExcelTask {
  operateTime: string
  exportType: number
  membersNum: number
  startDate: string
  endDate: string
  finished: number
  fileName: string
  url: string
  contentNumber?: number
  requestId: string
}

// Excel 导出结果类型
interface ExcelExportResult {
  exportResult: number
  fileName: string
  exportURL: string
  contentNumber?: number
}

const formatDuration = (seconds: number | undefined) => {
  if (seconds !== undefined) {
    let hours = Math.floor(seconds / 3600)
    let minutes = Math.round((seconds % 3600) / 60)
    return `${hours}小时${minutes}分钟`
  }
  return ''
}

const attendanceStatusText = (status: number | undefined) => {
  switch (status) {
    case 0:
      return '正常'
    case 1:
      return '迟到'
    case 2:
      return '早退'
    case 3:
      return '缺卡'
    case 4:
      return '尚未打卡'
    case 5:
      return '无需打卡'
    default:
      return ''
  }
}

function Period() {
  const dispatch = useAppDispatch()
  const groupId = useAppSelector((state) => state.group.groupId)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const members = useAppSelector((state) => state.group.members)
  const isVip = useAppSelector((state) => state.group.isVip)
  const myRole = useAppSelector((state) => state.group.myRole)

  const [attendanceData, setAttendanceData] = useState<PeriodData[]>([])
  const [recordUsers, setRecordUsers] = useState<RecordUser[]>([])
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [filter, setFilter] = useState<AttendanceFilter>({
    dates: [dayjs().startOf('months'), dayjs().endOf('days')],
    members: [],
    userDepartmentIDs: [],
    departments: []
  })

  useEffect(() => {
    handleFetchAttendance()
  }, [filter])

  const handleFilterChange = (newFilter: AttendanceFilter) => {
    setFilter(newFilter)
  }

  const handleFetchAttendance = () => {
    const {
      dates,
      members: filterMembers,
      userDepartmentIDs,
      departments
    } = filter
    const isDatesOutOfMaxRange =
      Math.abs(dates[0].diff(dates[1], 'days', true)) > 31
    const isStartTimeAfterEndTime = dates[0].isAfter(dates[1])
    const departmentIDList = departments.map((department) => department.id)
    if (!isDatesOutOfMaxRange && !isStartTimeAfterEndTime && groupId) {
      const params = {
        groupID: groupId,
        userID: userId,
        parameters: {
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          users: filterMembers,
          userIDList: userDepartmentIDs,
          departmentIdList: departmentIDList,
          page: {
            isPagination: true
          }
        }
      }
      dispatch(fetchAttendancePeriod(params))
        .then((data) => {
          setAttendanceData(data.timeDurationDatas || [])
          setRecordUsers(data.users || [])
          setCurrentPage(1)
        })
        .catch(SentryUtils.capturePromiseException)
    }
  }

  const handleCalcMemberPickedValue = () => {
    const userNumInDepartments = filter.departments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total =
      filter.members.length +
      filter.userDepartmentIDs.length +
      userNumInDepartments
    if (
      total === 0 ||
      total === members.length ||
      filter.departments.some((department) => department.id === '0')
    ) {
      return members.length
    } else {
      return total
    }
  }

  const recordExcelTask = ({
    exportType,
    membersNum,
    startDate,
    endDate,
    finished,
    fileName,
    url,
    contentNumber,
    operateTime,
    requestId
  }: Omit<ExcelTask, 'operateTime'> & { operateTime?: string }) => {
    let tasks: ExcelTask[] = []
    const excelTasks = localStorage.getItem('excelTasks')
    if (excelTasks) {
      try {
        tasks = JSON.parse(excelTasks)
      } catch (error) {
        console.log('解析Excel任务列表失败')
      }
    }
    tasks.push({
      operateTime: operateTime || dayjs().format(CONFIG.timeFormat),
      exportType,
      membersNum,
      startDate,
      endDate,
      finished,
      fileName,
      url,
      contentNumber,
      requestId
    })
    localStorage.setItem('excelTasks', JSON.stringify(tasks))
  }

  const triggerAttendanceExcelExport = (
    exportType: number,
    dates: [Dayjs, Dayjs],
    filterMembers: string[],
    userDepartmentIDs: string[],
    departmentIDList: string[]
  ) => {
    return dispatch(
      fetchAttendanceExcel({
        groupID: groupId,
        userID: userId,
        parameters: {
          users: filterMembers,
          userIDList: userDepartmentIDs,
          departmentIdList: departmentIDList,
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          exportType
        }
      })
    )
      .then((data) => {
        return data
      })
      .catch((error) => {
        SentryUtils.capturePromiseException(error)
        throw error
      })
  }

  const buildPhotoWatermarkDefaultColumns = () => {
    const baseColumns = [
      'userNickname',
      'watermarkName',
      'time',
      'addr',
      'weather',
      'lng',
      'lat',
      'coordinates',
      'altitude'
    ]
    return baseColumns
      .map((key) => {
        const column = baseColumnsData.find((item) => item.dataIndex === key)
        return column ? column.title : null
      })
      .filter(Boolean)
  }

  const encodeExportParams = (params: Record<string, unknown>) => {
    const jsonStr = JSON.stringify(params)
    if (typeof window !== 'undefined' && window.btoa) {
      return window.btoa(unescape(encodeURIComponent(jsonStr)))
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(jsonStr, 'utf-8').toString('base64')
    }
    return jsonStr
  }

  const buildPhotoWatermarkExportPayload = (
    dates: [Dayjs, Dayjs],
    filterMembers: string[],
    userDepartmentIDs: string[],
    departmentIDList: string[]
  ) => {
    const startTime = dates[0].clone().startOf('day').format(CONFIG.timeFormat)
    const endTime = dates[1].clone().endOf('day').format(CONFIG.timeFormat)
    const exportParams = {
      times: [
        {
          start: startTime,
          end: endTime
        }
      ],
      watermarks: [],
      keywords: [],
      mediaType: -1,
      users: filterMembers && filterMembers.length ? filterMembers : null,
      userIDList: userDepartmentIDs,
      departmentIDList:
        departmentIDList && departmentIDList.length ? departmentIDList : ['0'],
      existPhotoComment: 0,
      locations: null,
      photoTags: null
    }
    return {
      groupID: groupId,
      exportParams: encodeExportParams(exportParams),
      defaultTitles: buildPhotoWatermarkDefaultColumns(),
      extendTitles: [],
      type: 1,
      formatType: 1
    }
  }

  const triggerPhotoWatermarkExcelExport = (
    dates: [Dayjs, Dayjs],
    filterMembers: string[],
    userDepartmentIDs: string[],
    departmentIDList: string[]
  ) => {
    return dispatch(
      fetchPhotoWatermarkExcel(
        buildPhotoWatermarkExportPayload(
          dates,
          filterMembers,
          userDepartmentIDs,
          departmentIDList
        )
      )
    )
      .then((data) => {
        return data
      })
      .catch((error) => {
        SentryUtils.capturePromiseException(error)
        throw error
      })
  }

  const handleExcel = () => {
    if (!isVip) {
      dispatch(setPaymentVisible(true, 'attendancePeriodChooseDateExcel'))
      return
    }

    const {
      dates,
      members: filterMembers,
      userDepartmentIDs,
      departments
    } = filter
    const departmentIDList = departments.map((department) => department.id)
    const memberCount = handleCalcMemberPickedValue()
    sensors.track('web_workgroup_attandence_page_click_export_excel', {
      pagePath: window.location.pathname,
      start_date: dates[0].format(CONFIG.dateFormat),
      end_date: dates[1].format(CONFIG.dateFormat),
      timePeriod: dates[1].diff(dates[0], 'days') + 1,
      memberNum: memberCount,
      groupID: groupId,
      role: sensorsRole(myRole),
      isGroupVIP: isVip
    })

    // 并行发起三个导出请求，等所有请求结果后按指定顺序写入 localStorage（照片台账 -> 每日统计 -> 周月统计）
    const promises = [
      triggerPhotoWatermarkExcelExport(
        dates,
        filterMembers,
        userDepartmentIDs,
        departmentIDList
      ),
      triggerAttendanceExcelExport(
        DAILY_EXPORT_TYPE,
        dates,
        filterMembers,
        userDepartmentIDs,
        departmentIDList
      ),
      triggerAttendanceExcelExport(
        PERIOD_EXPORT_TYPE,
        dates,
        filterMembers,
        userDepartmentIDs,
        departmentIDList
      )
    ]

    Promise.allSettled(promises)
      .then((results) => {
        const [photoRes, dailyRes, periodRes] = results

        const requestId = `${dayjs().valueOf()}_${Math.random()
          .toString(36)
          .slice(2, 8)}`

        const pushIfFulfilled = (
          res: PromiseSettledResult<ExcelExportResult>,
          makeTask: (data: ExcelExportResult) => Omit<ExcelTask, 'requestId' | 'operateTime'> & { operateTime?: string }
        ) => {
          if (res && res.status === 'fulfilled' && res.value) {
            recordExcelTask({ ...makeTask(res.value), requestId })
          }
        }

        pushIfFulfilled(photoRes, (data) => ({
          exportType: PHOTO_WATERMARK_EXPORT_TYPE,
          membersNum: memberCount,
          startDate: dates[0].clone().startOf('day').format(CONFIG.timeFormat),
          endDate: dates[1].clone().endOf('day').format(CONFIG.timeFormat),
          finished: data.exportResult,
          fileName: data.fileName,
          url: data.exportURL,
          contentNumber: data.contentNumber,
          operateTime: dayjs().add(0, 'seconds').format(CONFIG.timeFormat)
        }))

        pushIfFulfilled(dailyRes, (data) => ({
          exportType: DAILY_EXPORT_TYPE,
          membersNum: memberCount,
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          finished: data.exportResult,
          fileName: data.fileName,
          url: data.exportURL,
          operateTime: dayjs().add(1, 'seconds').format(CONFIG.timeFormat)
        }))

        pushIfFulfilled(periodRes, (data) => ({
          exportType: PERIOD_EXPORT_TYPE,
          membersNum: memberCount,
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          finished: data.exportResult,
          fileName: data.fileName,
          url: data.exportURL,
          operateTime: dayjs().add(2, 'seconds').format(CONFIG.timeFormat)
        }))

        window.open('/attendance/export-list?fromPlace=attendancePeriod')
      })
      .catch((error) => {
        console.error('导出过程中出现错误:', error)
        window.open('/attendance/export-list?fromPlace=attendancePeriod')
      })
  }

  const handleCheckPhotos = (startTime: string, endTime: string, memberId: string) => {
    sensors.track('web_workgroup_attandence_page_click', {
      clickItem: 'photoNum',
      pagePath: window.location.pathname
    })
    window.open(
      `/photo?start_time=${encodeURIComponent(
        startTime
      )}&end_time=${encodeURIComponent(endTime)}&member_id=${encodeURIComponent(
        memberId
      )}`
    )
  }

  const getRecordDates = () => {
    if (!attendanceData || !attendanceData.length) {
      return []
    }
    const allDates = attendanceData
      .flatMap((item) => item.records || [])
      .map((record) => record.date)
    // 去重并排序
    return Array.from(new Set(allDates)).sort(
      (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
    )
  }

  const notNeedAttendance = (record: AttendanceRecord) => {
    if (record.bucket && record.bucket.length) {
      for (const item of record.bucket) {
        if (item.offWork?.status === 5) {
          return true
        }
      }
    }
    return false
  }

  const formatPunchText = (punch: WorkTime | undefined) => {
    if (!punch) {
      return ''
    }
    const timeText = punch.photoTime
    const statusText = attendanceStatusText(punch.status)
    if (timeText && statusText) {
      return `${timeText} ${statusText}`
    }
    if (timeText) {
      return timeText
    }
    return statusText || ''
  }

  const renderRecordCell = (record: PeriodData, date: string) => {
    const targetRecord =
      (record.records || []).find((item) => item.date === date) || {} as AttendanceRecord
    const bucket = targetRecord.bucket || []
    if (
      notNeedAttendance(targetRecord) ||
      targetRecord.status === 2 ||
      targetRecord.ruleChunkID === '-1'
    ) {
      return <div>-</div>
    } else if (bucket.length === 0) {
      return <div>未出勤</div>
    }

    return (
      <div>
        {bucket.map((item, index) => {
          const onText = formatPunchText(item.onWork)
          const offText = formatPunchText(item.offWork)
          return (
            <div key={index}>{(onText || '-') + '-' + (offText || '-')}</div>
          )
        })}
      </div>
    )
  }

  const renderRecordColumns = (recordDates: string[]) => {
    return recordDates.map((date) => (
      <Column<PeriodData>
        key={`record-${date}`}
        width={180}
        title={dayjs(date).format('MM月DD日')}
        render={(_, record) => renderRecordCell(record, date)}
      />
    ))
  }

  const { dates } = filter
  const recordDates = getRecordDates()
  const dataSource = attendanceData
    ? attendanceData
        .slice(pageSize * (currentPage - 1), pageSize * currentPage)
        .map((row, index) => {
          return {
            key: index,
            ...row
          }
        })
    : []

  return (
    <Layout className="Attendance">
      <PageHeader
        ghost={false}
        style={{ borderBottom: '1px solid rgb(235, 237, 240)' }}
        title="周月统计"
        extra={[
          <Button
            key="0"
            type="link"
            onClick={() =>
              window.open('/attendance/export-list?fromPlace=attendancePeriod')
            }
          >
            下载记录
          </Button>,
          <Button
            key="1"
            type="primary"
            disabled={!attendanceData.length}
            onClick={handleExcel}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              下载表格Excel
              <img
                src={vip_download_excel_icon}
                alt=""
                style={{
                  marginLeft: '4px',
                  width: 20,
                  height: 20
                }}
              />
            </div>
          </Button>
        ]}
      />
      <Content className="content">
        <Filter filter={filter} onChange={handleFilterChange} />
        <div className="table-wrapper">
          <Table
            bordered
            scroll={{
              y: window.innerHeight - 420,
              x: 1430,
              scrollToFirstRowOnChange: true
            }}
            pagination={{
              style: { marginTop: '20px', float: 'right' },
              total: attendanceData.length,
              showTotal: (total) => `共 ${total} 条`,
              showSizeChanger: true,
              pageSizeOptions: ['20', '30', '50'],
              pageSize: pageSize,
              onShowSizeChange: (current, size) => {
                setPageSize(size)
                setCurrentPage(current)
              },
              current: currentPage,
              onChange: (current, size) => {
                setPageSize(size)
                setCurrentPage(current)
              }
            }}
            dataSource={dataSource}
          >
            <Column
              title="姓名"
              fixed="left"
              width={180}
              dataIndex="userID"
              render={(text, record) => {
                const curUser = recordUsers.find((user) => user.userID === text)
                return curUser ? curUser.userName : ''
              }}
            />
            <Column
              title="考勤规则"
              width={120}
              dataIndex="ruleChunkName"
              render={(text, record) => {
                return record.ruleChunkName
              }}
            />
            <Column
              title="总拍照张数"
              width={120}
              dataIndex="totalPhotoCount"
              render={(text, record) => {
                if (!!text) {
                  return (
                    <a
                      onClick={() =>
                        handleCheckPhotos(
                          `${dates[0]
                            .startOf('days')
                            .format(CONFIG.minuteFormat)}`,
                          `${dates[1]
                            .endOf('days')
                            .format(CONFIG.minuteFormat)}`,
                          record.userID
                        )
                      }
                    >
                      {text}
                    </a>
                  )
                } else {
                  return <span>{text}</span>
                }
              }}
            />
            <Column title="出勤天数" width={100} dataIndex="onworkDay" />
            <Column title="未出勤天数" width={120} dataIndex="unworkDay" />
            <Column
              title="工作时长合计"
              width={120}
              dataIndex="totalWorkSecond"
              render={(text, record) => formatDuration(text)}
            />
            <Column
              title="迟到（次）"
              width={110}
              dataIndex="lateCount"
              render={(text, record) =>
                text !== 0 ? (
                  <a
                    onClick={() =>
                      sensors.track('web_workgroup_attandence_page_click', {
                        clickItem: 'late',
                        pagePath: window.location.pathname
                      })
                    }
                    style={{ color: '#E02020' }}
                    href={`/attendance/calendar?user_id=${
                      record.userID
                    }&date=${encodeURIComponent(
                      dates[0].format(CONFIG.dateFormat)
                    )}`}
                  >
                    {text}
                  </a>
                ) : (
                  <span>{text}</span>
                )
              }
            />
            <Column
              title="早退（次）"
              width={110}
              dataIndex="leaveEarlyCount"
              render={(text, record) =>
                text !== 0 ? (
                  <a
                    onClick={() =>
                      sensors.track('web_workgroup_attandence_page_click', {
                        clickItem: 'leaveEarly',
                        pagePath: window.location.pathname
                      })
                    }
                    style={{ color: '#E02020' }}
                    href={`/attendance/calendar?user_id=${
                      record.userID
                    }&date=${encodeURIComponent(
                      dates[0].format(CONFIG.dateFormat)
                    )}`}
                  >
                    {text}
                  </a>
                ) : (
                  <span>{text}</span>
                )
              }
            />
            <Column
              title="缺卡（次）"
              width={110}
              dataIndex="cardShortageCount"
              render={(text, record) =>
                text !== 0 ? (
                  <a
                    onClick={() =>
                      sensors.track('web_workgroup_attandence_page_click', {
                        clickItem: 'lackRecord',
                        pagePath: window.location.pathname
                      })
                    }
                    style={{ color: '#E02020' }}
                    href={`/attendance/calendar?user_id=${
                      record.userID
                    }&date=${encodeURIComponent(
                      dates[0].format(CONFIG.dateFormat)
                    )}`}
                  >
                    {text}
                  </a>
                ) : (
                  <span>{text}</span>
                )
              }
            />
            <Column
              title="工时异常（次）"
              width={140}
              dataIndex="abnormalWorkingHoursCount"
              render={(text, record) =>
                text !== 0 ? (
                  <a
                    onClick={() =>
                      sensors.track('web_workgroup_attandence_page_click', {
                        clickItem: 'abnormalWorkHours',
                        pagePath: window.location.pathname
                      })
                    }
                    style={{ color: '#E02020' }}
                    href={`/attendance/calendar?user_id=${
                      record.userID
                    }&date=${encodeURIComponent(
                      dates[0].format(CONFIG.dateFormat)
                    )}`}
                  >
                    {text}
                  </a>
                ) : (
                  <span>{text}</span>
                )
              }
            />
            <Column title="请假天数" width={100} dataIndex="leaveCount" />
            <Column
              title="请假时长"
              width={100}
              dataIndex="leaveDurationText"
              render={(leaveDurationText, record) => {
                if (record.leaveCount) {
                  return leaveDurationText
                }

                return '-'
              }}
            />
            {renderRecordColumns(recordDates)}

            <Column
              width={100}
              title="更多"
              fixed="right"
              render={(record) => (
                <a
                  style={{ textDecoration: 'none' }}
                  onClick={() =>
                    sensors.track('web_workgroup_attandence_page_click', {
                      clickItem: 'attendanceCalendar',
                      pagePath: window.location.pathname
                    })
                  }
                  href={`/attendance/calendar?user_id=${
                    record.userID
                  }&date=${encodeURIComponent(
                    dates[0].format(CONFIG.dateFormat)
                  )}`}
                >
                  考勤日历
                </a>
              )}
            />
          </Table>
        </div>
      </Content>
    </Layout>
  )
}

export default Period
